

import { Component, Vue, Watch } from "vue-property-decorator"
import { namespace, Getter, Action, State } from "vuex-class"

import { SystemAuditType, SystemAuditTypeLabels, SystemAudit } from "@/types/audit"

import vSelect from "vue-select"
import SkeletonLoader from "@/components/SkeletonLoader.vue"
import UserSelect from "@/components/Controls/UserSelect.vue"
import UserImage from "@/components/UserImage.vue"
// @ts-ignore
import VDatePicker from "v-calendar/lib/components/date-picker.umd"

import api from "@/services/api"
import { TemplateDetails, Field } from "@/types"
import { DateTime } from "luxon"
import { UserWithFields } from "@/types/user"
import { TenantFeatures } from "@/types/tenantFeatures"
import DataCache from "@/caching/caching"

const caseStore = namespace("cases")
const setupStore = namespace("setup")

interface SearchItem {
	id: string
	title: string
}

interface DateRange {
	start: Date | null
	end: Date | null
}
interface DateRangeIso {
	start: string | null
	end: string | null
}

@Component({
	components: {
		"skeleton": SkeletonLoader,
		"v-select": vSelect,
		"user-select": UserSelect,
		"user-image": UserImage,
		"date-picker": VDatePicker
	}
})

export default class AuditView extends Vue {
	$refs!: {
		scrollContainer: HTMLElement
	}

	// --- VueX ---

	@State private readonly userCache!: DataCache<UserWithFields>

	@caseStore.State categories!: TemplateDetails[] | null
	@caseStore.Action refreshCategories!: () => Promise<void>

	@setupStore.State fields!: Field[]
	@setupStore.Action loadFieldsIfUnloaded!: () => Promise<void>

	// --- Data ---

	userAuditTypes: SystemAuditType[] = []
	auditEntries: SystemAudit[] = []

	searching = false
	doneSearch = false
	loadingMore = false
	moreMightBeAvailable = false

	searchType: string | null = null
	searchId: string | null = null
	searchUser: string | null = null
	searchRange: { start: Date | null, end: Date | null } = {
		start: null,
		end: null
	}

	currentSearch: {
		searchType: string | null
		searchId: string | null
		searchUser: string | null
		searchRange: DateRangeIso
	} | null = null

	SystemAuditTypeLabels = SystemAuditTypeLabels // Allow use in template

	@Getter("getIsFeatureEnabled") getIsFeatureEnabled!: (feature: TenantFeatures) => boolean
	tenantFeatures = TenantFeatures

	// Table columns
	columns = [
		{
			key: "changedAt"
		},
		{
			key: "objectType",
			label: "Type"
		},
		{
			key: "changedBy"
		},
		{
			key: "objectTitle",
			label: "Changed Item"
		},
		{
			key: "change",
			tdClass: "change-details"
		}
	]

	changeItems: SearchItem[] = []

	typesSupportingItems = [
		SystemAuditType.Field,
		SystemAuditType.Template,
		SystemAuditType.User,
		SystemAuditType.Team,
		SystemAuditType.View,
		SystemAuditType.Insight,
		SystemAuditType.Definition,
		SystemAuditType.DataSource,
		SystemAuditType.Schedule
	]

	// --- Computed Properties ---

	public get userAuditTypeLabels (): { value: SystemAuditType, label: string }[] {
		return this.userAuditTypes.map(type => ({
			value: type,
			label: SystemAuditTypeLabels[type]
		})).sortBy("label")
	}

	public get hasDateRange (): boolean {
		return !!this.searchRange.start && !!this.searchRange.end
	}

	public get typeItemSupported (): boolean {
		return !!this.searchType && this.typesSupportingItems.includes(this.searchType as SystemAuditType)
	}

	// --- Lifecycle Hooks ---

	async mounted () {
		// Find the audit entry types the current user is allowed to see
		this.userAuditTypes = await api.systemAuditTypes()
	}

	// --- Event Handlers ---

	/**
	* @summary Perform a search for system audit entries based on the specified search criteria.
	*/
	async doSearch (): Promise<void> {
		if (this.searching || this.loadingMore) return

		this.searching = true
		this.doneSearch = true

		this.auditEntries = []

		try {
			// Using local time, start / end should be the start and end of the day respectively
			const start = this.searchRange.start ? DateTime.fromJSDate(this.searchRange.start).startOf("day").toISO() : null
			const end = this.searchRange.end ? DateTime.fromJSDate(this.searchRange.end).endOf("day").toISO() : null

			// Save search params for fetching more
			this.currentSearch = {
				searchType: this.searchType,
				searchUser: this.searchUser,
				searchRange: {
					start,
					end
				},
				searchId: this.searchId
			}

			// Use API retrieve audit entries
			this.auditEntries = await api.systemAuditSearch(
				this.searchType,
				this.searchUser,
				start && end
					? {
						start,
						end
					}
					: null,
				this.searchId
			)

			// If this is at max page size, there might be more available
			this.moreMightBeAvailable = this.auditEntries.length === 50
		} finally {
			this.searching = false
		}
	}

	/**
	* @summary When the search type changes, clear the search items and fetch the new ones
	*/
	async searchTypeChanged () {
		this.searchId = null
		this.changeItems = []

		switch (this.searchType) {
			case SystemAuditType.Field:
				if (this.fields.length === 0) {
					await this.loadFieldsIfUnloaded()
				}
				this.changeItems = (this.fields ?? []) as SearchItem[]
				break
			case SystemAuditType.Template:
				if (!this.categories) {
					await this.refreshCategories()
				}
				this.changeItems = (this.categories ?? []) as SearchItem[]
				break
			case SystemAuditType.Definition:
				this.changeItems = (await api.definitions()) as SearchItem[]
				break
			case SystemAuditType.User:
				await this.userCache.fetchIfNeeded()
				this.changeItems = (this.userCache.items ?? []).map(u => ({ id: u.id, title: u.name }))
				break
			case SystemAuditType.Team:
				this.changeItems = await api.teamsGet()
				break
			case SystemAuditType.View:
				this.changeItems = await api.caseViewsGet()
				break
			case SystemAuditType.Insight:
				this.changeItems = await api.insightsGet()
				break
			case SystemAuditType.Schedule:
				this.changeItems = (await api.retrieveSchedules()) as SearchItem[]
				break
			case SystemAuditType.DataSource:
				this.changeItems = (await api.dataSources()) as SearchItem[]
				break
		}
	}

	/**
	* @summary When scrolling to the end, fetch some more
	*/
	async checkScroll () {
		// Do not do anything if: If we are already loading more, if we haven't searched yet, we know there's no more to get, or we haven't got the original search params
		if (this.loadingMore || !this.doneSearch || !this.moreMightBeAvailable || !this.currentSearch) return

		// Check if the scroll container is scrolled to the bottom
		const scrollContainer = this.$refs.scrollContainer
		if (Math.round(scrollContainer.scrollTop + scrollContainer.clientHeight) >= scrollContainer.scrollHeight) {
			this.loadingMore = true // Set to load more to prevent double-fetch

			try {
				// Times for new entries - start within current date range ...
				const start = this.currentSearch.searchRange.start ? this.currentSearch.searchRange!.start : null
				let end = this.currentSearch.searchRange.end ? this.currentSearch.searchRange!.end : null

				if (this.auditEntries && this.auditEntries.length > 0) {
					// Move search time so that we get the next block of older entries
					end = this.auditEntries[this.auditEntries.length - 1].changedAt
				}

				const newEntries = await api.systemAuditSearch(
					this.currentSearch.searchType,
					this.currentSearch.searchUser,
					{ start: start, end: end },
					this.currentSearch.searchId)
				this.moreMightBeAvailable = newEntries.length === 50

				this.auditEntries.push(...newEntries) // Append new items to existing list
			} finally {
				// Even on error, clear this afterwards
				this.loadingMore = false
			}
		}
	}

	/**
	* @summary When the calendar runs "updateValue" it tries to set data range to null, which breaks.
	* This prevents that from happening.
	*/
	searchRangeChanged (newDateRange: DateRange | null) {
		if (newDateRange) {
			this.searchRange = newDateRange
		} else {
			this.searchRange.start = null
			this.searchRange.end = null
		}
	}
}

